import { Tag } from "@hortis/ui/tag";
import {
  type AutocompleteRenderGetTagProps,
  createFilterOptions,
} from "@mui/material/Autocomplete";
import { useRouteContext } from "@tanstack/react-router";
import {
  type LifeForm,
  useGetOrgLifeFormsQuery,
  useGetSiteLifeFormsQuery,
} from "generated/graphql";
import { colors } from "src/colors";
import { FormAutocomplete } from "src/components/autocomplete";
import { usePlaceStruct } from "src/utils/hooks/place";

export const filterOptions = createFilterOptions({
  stringify: (option: LifeForm) => option.name,
});

export const renderTags = (
  tags: Array<LifeForm>,
  getTagProps: AutocompleteRenderGetTagProps,
) => (
  <div className="flex flex-wrap">
    {tags.map((tag, index) => (
      <Tag {...getTagProps({ index })} key={tag.id}>
        {tag.name}
      </Tag>
    ))}
  </div>
);

interface LifeFormsAutocompleteProps {
  name: string;
  testId?: string;
  options: ReadonlyArray<LifeForm>;
  loading?: boolean;
}

const LifeFormsAutocomplete = (props: LifeFormsAutocompleteProps) => {
  return (
    <FormAutocomplete
      {...props}
      multiple
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={filterOptions}
      label={null}
      sx={{
        "& label": { display: "none" },
        "& .MuiOutlinedInput-root": { py: "4px" },
        "& .MuiOutlinedInput-notchedOutline": {
          transition: `all 125ms ease`,
          border: `1px solid ${colors.grey[300]}`,
          boxShadow: `0 0 0 0px ${colors.primary[100]}`,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: `1px solid ${colors.primary[300]}`,
          boxShadow: `0 0 0 4px ${colors.primary[100]}`,
        },
      }}
      renderTags={renderTags}
      fullWidth
      componentsProps={{ popper: { disablePortal: true } }}
    />
  );
};

export const OrgLifeFormAutocomplete = ({
  name,
  testId,
}: Pick<LifeFormsAutocompleteProps, "name" | "testId">) => {
  const { subdomain } = useRouteContext({ from: "__root__" });
  const pause = subdomain == null;
  const [{ data, fetching }] = useGetOrgLifeFormsQuery({
    variables: {
      organisationSubdomain: subdomain ?? "",
    },
    pause,
  });
  const lifeForms = data?.org?.lifeForms?.nodes;

  return (
    <LifeFormsAutocomplete
      name={name}
      testId={testId}
      loading={fetching}
      options={lifeForms ?? []}
    />
  );
};

export const SiteLifeFormAutocomplete = ({
  name,
  testId,
}: Pick<LifeFormsAutocompleteProps, "name" | "testId">) => {
  const place = usePlaceStruct();
  const pause = place.data == null;
  const [{ data, fetching }] = useGetSiteLifeFormsQuery({
    variables: {
      organisationSubdomain: place.data?.orgName ?? "",
      collectionSiteSlug: place.data?.siteSlug ?? "",
    },
    pause,
  });
  const lifeForms = data?.org?.site?.lifeForms?.nodes;

  return (
    <LifeFormsAutocomplete
      name={name}
      testId={testId}
      loading={fetching || place.loading}
      options={lifeForms ?? []}
    />
  );
};
